import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Timeline, TimelineItem } from "@/components/ui/timeline";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { buildCompanyEvents, type CompanyFeedEvent } from "@/lib/company-events";
import { formatDateLong, formatDateShortMonth } from "@/lib/format-amount";
import { CompanyDTO } from "@shaarp/common";
import {
  CalendarClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import { useState } from "react";

/** Events per page: the latest ones, on the first, are the signal. */
const EVENTS_PER_PAGE = 6;

type CardCompanyEventsProps = {
  readonly company: CompanyDTO;
};

function EventDate({ event }: { readonly event: CompanyFeedEvent }) {
  const { currentLanguage } = useTranslation();
  // A LinkedIn move is dated to the month: shown as such, never as the 1st.
  const label =
    event.date.length === 7
      ? formatDateShortMonth(`${event.date}-01`, currentLanguage)
      : formatDateLong(event.date, currentLanguage);
  return <span className="text-xs text-muted-foreground">{label}</span>;
}

/**
 * The company's events feed: its BODACC notices (directors, capital, head
 * office, sales, proceedings) and its key executives' LinkedIn moves.
 */
export default function CardCompanyEvents({ company }: CardCompanyEventsProps) {
  const { t, currentLanguage } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();
  const [pageIndex, setPageIndex] = useState(0);
  const events = buildCompanyEvents(company, arePeopleVisible);

  if (events.length === 0) return null;

  const pageCount = Math.ceil(events.length / EVENTS_PER_PAGE);
  // A refreshed company can bring fewer events: never point past the last page.
  const page = Math.min(pageIndex, pageCount - 1);
  const shown = events.slice(
    page * EVENTS_PER_PAGE,
    (page + 1) * EVENTS_PER_PAGE,
  );

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitleWithIcon
          icon={CalendarClockIcon}
          title={t("company.insights.events.title")}
        />
        <p className="text-xs text-muted-foreground pl-[52px] -mt-1">
          {t("company.insights.events.subtitle")}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Timeline>
          {shown.map((event) => (
            <TimelineItem key={event.key}>
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <EventDate event={event} />
                  <Badge variant="secondary">
                    {t(`company.insights.events.kinds.${event.kind}`)}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">
                    {t(`company.insights.events.sources.${event.source}`)}
                  </span>
                </div>
                {event.title && (
                  <span className="text-sm font-semibold break-words">
                    {event.title}
                  </span>
                )}
                {event.detail && (
                  <span className="text-xs text-muted-foreground break-words line-clamp-3">
                    {event.kind === "accounts"
                      ? t("company.insights.events.accountsClosing", {
                          date: formatDateLong(event.detail, currentLanguage),
                        })
                      : event.detail}
                  </span>
                )}
              </div>
            </TimelineItem>
          ))}
        </Timeline>
        {pageCount > 1 && (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-muted-foreground">
              {t("components.dataTable.pagination.page", {
                page: page + 1,
                total: pageCount,
              })}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              className="max-sm:size-11"
              aria-label={t("components.dataTable.pagination.previousPage")}
              disabled={page === 0}
              onClick={() => setPageIndex(page - 1)}
            >
              <ChevronLeftIcon />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              className="max-sm:size-11"
              aria-label={t("components.dataTable.pagination.nextPage")}
              disabled={page === pageCount - 1}
              onClick={() => setPageIndex(page + 1)}
            >
              <ChevronRightIcon />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
